/**
 * RDF-/DCAT-Export + SPARQL-Hinweis.
 *
 * Macht den (öffentlichen) DCAT-AP-EU-3.0-RDF-Export in der Oberfläche sichtbar:
 * ein Dropdown mit den vier Serialisierungen (Turtle / N-Triples / JSON-LD /
 * RDF-XML) plus einem Eintrag, der den öffentlichen SPARQL-Endpoint kopiert.
 *
 * Ohne `datasetId` exportiert es den gesamten Katalog
 * (`/api/catalog/export/dcat-ap`), mit `datasetId` das einzelne Dataset.
 */
import { Download, FileCode2 } from 'lucide-react';
import { toast } from 'sonner';

import { config as runtimeConfig } from '../lib/config.js';
import { Button } from './ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.js';

const FORMATS: Array<{ format: string; ext: string; label: string }> = [
  { format: 'ttl', ext: 'ttl', label: 'Turtle (.ttl)' },
  { format: 'nt', ext: 'nt', label: 'N-Triples (.nt)' },
  { format: 'jsonld', ext: 'jsonld', label: 'JSON-LD (.jsonld)' },
  { format: 'rdfxml', ext: 'rdf', label: 'RDF/XML (.rdf)' },
];

export function RdfExport({
  datasetId,
  slug,
  size = 'sm',
}: {
  datasetId?: string;
  slug?: string;
  size?: 'sm' | 'default';
}): JSX.Element {
  const base = `${runtimeConfig.apiBaseUrl}/api/catalog/export/dcat-ap${
    datasetId ? `/datasets/${datasetId}` : ''
  }`;
  const fileBase = slug ?? (datasetId ? 'dataset' : 'catalog');
  const sparqlUrl = `${runtimeConfig.apiBaseUrl || window.location.origin}/api/catalog/sparql`;

  const copySparql = () => {
    void navigator.clipboard
      ?.writeText(sparqlUrl)
      .then(() => toast.success('SPARQL-Endpoint kopiert', { description: sparqlUrl }))
      .catch(() => toast.error('Konnte nicht kopieren'));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size={size}>
          <FileCode2 className="mr-1 h-3.5 w-3.5" />
          Als RDF
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          {datasetId ? 'Datensatz als RDF' : 'Katalog als RDF'} (DCAT-AP EU 3.0)
        </DropdownMenuLabel>
        {FORMATS.map((f) => (
          <DropdownMenuItem key={f.format} asChild>
            <a href={`${base}?format=${f.format}`} download={`${fileBase}.${f.ext}`}>
              <Download className="mr-2 h-3.5 w-3.5" />
              {f.label}
            </a>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={copySparql}>
          <FileCode2 className="mr-2 h-3.5 w-3.5" />
          SPARQL-Endpoint kopieren
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
